import { UserIntentModal } from "@/components/auth/UserIntentModal";
import { LanguageSelector } from "@/components/base";
import { PasswordField } from "@/components/base/PasswordField";
import { useTranslation } from "@/hooks/useTranslation";
import { useLogin } from "@/services/auth";
import { currentUserAtom } from "@/store/login";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Card,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Typography,
	buttonVariants,
	toast,
} from "@st-marketplace/ui";
import { useNavigate } from "@tanstack/react-router";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

/** Teams deep link to support, with the message pre-filled. */
const teamsChat = (message: string) =>
	`https://teams.microsoft.com/l/chat/0/0?users=mani_nikou@yahoo.com&message=${encodeURIComponent(
		message,
	)}`;

const SIGNUP_CHAT_URL = teamsChat("I want to register");
const RESET_CHAT_URL = teamsChat("I want to reset my password");

const signInSchema = z.object({
	username: z.string().min(1),
	password: z.string().min(1),
});

type SignInFormData = z.infer<typeof signInSchema>;

export default function SignIn() {
	const { t, isRtl } = useTranslation();
	const loginMutation = useLogin();
	const loggedInUser = useAtomValue(currentUserAtom);
	const setCurrentUser = useSetAtom(currentUserAtom);
	const navigate = useNavigate();

	useEffect(() => {
		if (loggedInUser && loggedInUser.user_role === "FC_USER") {
			toast.error(t("auth.accessDenied"));
			setCurrentUser(null);
		}
	}, [loggedInUser, setCurrentUser, t]);

	const form = useForm<SignInFormData>({
		resolver: zodResolver(signInSchema),
		defaultValues: { username: "", password: "" },
		mode: "onChange",
	});

	const canSubmit = !loginMutation.isPending;

	const onSubmit = (data: SignInFormData) => {
		loginMutation.mutate(
			{ username: data.username, passwd: data.password },
			{
				onSuccess: (user) => {
					toast.success(t("auth.welcome"));
					if (user.user_role === "ADMIN") {
						navigate({ to: "/st-admin/dashboard" });
					} else {
						navigate({ to: "/dashboard" });
					}
				},
			},
		);
	};

	return (
		<main
			className="relative min-h-dvh bg-background-50 px-5 py-24 sm:px-10 grid place-items-center"
			style={{
				backgroundImage:
					"radial-gradient(ellipse at top, color-mix(in srgb, var(--color-primary-100) 16%, transparent), transparent 70%)",
			}}
		>
			<div className="absolute top-6 inset-x-5 sm:inset-x-10 flex items-center justify-between gap-3">
				<div className="flex items-center gap-2.5 text-secondary-text">
					<img
						src="/snipe-transfer-logo.png"
						alt=""
						className="size-9 object-contain"
					/>
					<Typography variant="body-lg" as="span" className="font-semibold">
						{t("logo.name")}
					</Typography>
				</div>
				<LanguageSelector compact />
			</div>
			<div className="relative w-full max-w-[480px]">
				<Card className="rounded-[28px] bg-background-0/90 px-6 py-9 shadow-lg sm:p-10">
					<header className="mb-9 text-center">
						<div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-2xl border border-secondary-border bg-background-50 text-secondary-text shadow-sm">
							<Icon name="login-box-line" className="size-7" />
						</div>
						<Typography
							variant="title-lg"
							as="h1"
							className="text-secondary-text"
						>
							{t("auth.signin")}
						</Typography>
						<Typography
							variant="body-md"
							className="mt-3 leading-7 text-secondary-icon"
						>
							{t("auth.enterCredentials")}
						</Typography>
					</header>
					<Form {...form}>
						<form
							noValidate
							onSubmit={form.handleSubmit(onSubmit)}
							className="space-y-4"
						>
							<FormField
								control={form.control}
								name="username"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="sr-only" required>
											{t("auth.username")}
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												value={field.value ?? ""}
												ltr
												size="lg"
												autoComplete="username"
												className={isRtl ? "placeholder:text-right" : undefined}
												placeholder={t("auth.username")}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<PasswordField
								name="password"
								label={t("auth.password")}
								control={form.control}
								required
								dir="ltr"
								autoComplete="current-password"
								size="lg"
								hideLabel
							/>
							<div className="pb-3 text-start">
								<a
									href={RESET_CHAT_URL}
									target="_blank"
									rel="noopener noreferrer"
									className={buttonVariants({
										variant: "text",
										color: "primary",
										size: "sm",
										className: "underline underline-offset-4",
									})}
								>
									{t("auth.forgotPasswordLink")}
								</a>
							</div>
							<Button
								type="submit"
								size="lg"
								loading={!canSubmit}
								disabled={!canSubmit}
								className="w-full"
							>
								{t("auth.signin")}
							</Button>
						</form>
					</Form>
					<Typography
						variant="body-md"
						className="mt-8 text-center text-secondary-icon"
					>
						{t("auth.dontHaveAccount")}{" "}
						<a
							href={SIGNUP_CHAT_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary-100 hover:underline"
						>
							{t("auth.signup")}
						</a>
					</Typography>
				</Card>
			</div>
			<UserIntentModal />
		</main>
	);
}
